/**
 * Push Notification Service
 * 
 * Handles browser push notifications and WebSocket real-time updates.
 * Uses the Browser Push API for notifications even when tab is closed.
 */

// Check if push notifications are supported
export const isPushSupported = () => {
  return 'Notification' in window && 'serviceWorker' in navigator && 'PushManager' in window;
};

// Request notification permission
export const requestNotificationPermission = async () => {
  if (!isPushSupported()) {
    console.warn('Push notifications not supported');
    return 'unsupported';
  }
  
  const permission = await Notification.requestPermission();
  return permission; // 'granted', 'denied', or 'default'
};

// Get current permission status
export const getNotificationPermission = () => {
  if (!isPushSupported()) return 'unsupported';
  return Notification.permission;
};

// Show a local notification (for testing or fallback)
export const showLocalNotification = (title, options = {}) => {
  if (Notification.permission !== 'granted') {
    console.warn('Notification permission not granted');
    return null;
  }
  
  const defaultOptions = {
    icon: '/dealshaq-icon.png',
    badge: '/dealshaq-icon.png',
    vibrate: [200, 100, 200],
    requireInteraction: false,
    ...options
  };
  
  return new Notification(title, defaultOptions);
};

// WebSocket connection manager
class NotificationWebSocket {
  constructor() {
    this.ws = null;
    this.token = null;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectDelay = 1000;
    this.listeners = new Map();
    this.isConnecting = false;
  }
  
  connect(token) {
    if (this.isConnecting || (this.ws && this.ws.readyState === WebSocket.OPEN)) {
      return;
    }
    
    this.token = token;
    this.isConnecting = true;
    
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const host = process.env.REACT_APP_BACKEND_URL?.replace(/^https?:\/\//, '') || window.location.host;
    const wsUrl = `${protocol}//${host}/api/ws/notifications?token=${token}`;
    
    try {
      this.ws = new WebSocket(wsUrl);
      
      this.ws.onopen = () => {
        console.log('WebSocket connected for notifications');
        this.isConnecting = false;
        this.reconnectAttempts = 0;
        this.emit('connected', { status: 'connected' });
      };
      
      this.ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          this.handleMessage(data);
        } catch (e) {
          console.error('Failed to parse WebSocket message:', e);
        }
      };
      
      this.ws.onclose = (event) => {
        console.log('WebSocket closed:', event.code, event.reason);
        this.isConnecting = false;
        this.emit('disconnected', { code: event.code, reason: event.reason });
        
        // Attempt reconnection
        if (this.token && this.reconnectAttempts < this.maxReconnectAttempts) {
          this.reconnectAttempts++;
          const delay = this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1);
          console.log(`Reconnecting in ${delay}ms (attempt ${this.reconnectAttempts})`);
          setTimeout(() => this.connect(this.token), delay);
        }
      };
      
      this.ws.onerror = (error) => {
        console.error('WebSocket error:', error);
        this.isConnecting = false;
        this.emit('error', { error });
      };
      
    } catch (error) {
      console.error('Failed to create WebSocket:', error);
      this.isConnecting = false;
    }
  }
  
  handleMessage(data) {
    const { type } = data;
    
    // Emit to specific type listeners
    this.emit(type, data);
    
    // Emit to 'all' listeners
    this.emit('all', data);
    
    // Show browser notification for important types
    if (['new_deal', 'price_drop', 'low_stock'].includes(type) && data.title) {
      showLocalNotification(data.title, {
        body: data.body,
        data: data.data,
        tag: data.tag || type
      });
    }
  }
  
  disconnect() {
    this.token = null;
    this.reconnectAttempts = this.maxReconnectAttempts; // Prevent reconnection
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
  }
  
  send(message) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(message));
    }
  }
  
  // Send ping to keep connection alive
  ping() {
    this.send({ type: 'ping' });
  }
  
  // Acknowledge receipt of notification
  acknowledge(notificationId) {
    this.send({ type: 'ack', id: notificationId });
  }
  
  // Event listener management
  on(event, callback) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event).add(callback);
    
    // Return unsubscribe function
    return () => {
      this.listeners.get(event)?.delete(callback);
    };
  }
  
  off(event, callback) {
    this.listeners.get(event)?.delete(callback);
  }
  
  emit(event, data) {
    this.listeners.get(event)?.forEach(callback => {
      try {
        callback(data);
      } catch (e) {
        console.error('Notification listener error:', e);
      }
    });
  }
  
  isConnected() {
    return this.ws && this.ws.readyState === WebSocket.OPEN;
  }
}

// Singleton instance
export const notificationWS = new NotificationWebSocket();

export default notificationWS;
